import ProductsActionCreators from "@/Actions/Products";
import SettingsActionCreators from "@/Actions/Settings";
import UsersActionCreators from "@/Actions/User";
import RenderCustomFieldFormItem from "@molecules/ZRenderCustomFields";
import RoleBasedDisplayWrapper from "@/Components/UI/RoleBasedDisplayWrapper";
import { isEtilizeVendorProductId } from "@/Components/Utils";
import { useFeatureFlag } from "@/Hooks/useFeature";
import {
	handleMarginChange,
	handleMarkupChange,
	handleMarkupMargin,
	handleSellPriceCalculation
} from "@/Components/Utils/PricingCalculator";
import { ZeroOrNegative as AllowZeroOrNegative } from "@/Hooks/Integrations/interface";
import { useAllowZeroOrNegativeQuantity } from "@/Hooks/Integrations/useAllowZeroOrNegativeQuantity";
import { useAddOrEditSupplier } from "@/Hooks/Supplier/useAddOrEditSupplier";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { GlobalState } from "@/Reducers/interface";
import {
	getProductDetails,
	getQuoteBlockProductPricing
} from "@/Services/Products";
import { Form, Icon } from "@ant-design/compatible";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import ZFeatureAccess from "@atoms/ZFeatureAccess";
import round from "@molecules/ConfigSellPriceRoundingOption/RoundingUtils";
import TaxSelect from "@molecules/TaxSelect";
import ZSupplierSelect from "@organisms/ZSupplierSelect";
import ProductUOMWarning from "@pages/Documents/DocumentDetailsPage/molecules/ProductUOMWarning";
import AddProductDrawerV2 from "@pages/Drawers/Products/Product";
import { EProductDrawerState } from "@pages/Drawers/Products/Product/interface";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import ZAddSupplier from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier";
import { SupplierFormValues } from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier/interface";
import { UploadResult } from "@uppy/core";
import { ZInputNumber } from "@atoms";
import { EColors } from "@atoms/ZColors/interface";
import ZIcon, { Icons } from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ZImage from "@atoms/ZImage";
import ZModal from "@atoms/ZModal";
import { IModalProps } from "@atoms/ZModal/interface";
import ZSelect from "@atoms/ZSelect";
import ZTooltip from "@atoms/ZTooltip";
import ZTypography from "@atoms/ZTypography";
import ZCheckbox from "@atoms/ZCheckbox";
import ZCheckboxGroup from "@atoms/ZCheckboxGroup";
import {
	capitalizeFirstLetter,
	DefaultErrorMessage,
	fallbackImageURL,
	UppyFileMetaData
} from "@/Contracts";
import { Document, DocumentStatus } from "@/Contracts/Documents";
import {
	Distributor,
	distributorLabel,
	ProductBillingType,
	productBillingTypeOptions,
	ProductPricingPlan,
	ProductSource,
	ProductType,
	ratailPriceLabel,
	RetailPriceInfo
} from "@/Contracts/Products";
import {
	AddQuoteProductCatalogDrawerFormValues,
	BundledQuoteProduct,
	EditQuoteProductCatalogDrawerFormValues,
	QuoteProduct
} from "@/Contracts/ProposalBuilder/Block";
import {
	CustomField,
	CustomFieldMandatoryFieldDetailsValues,
	DynamicPricingDistributors,
	RoundingDetails,
	Supplier,
	SyncOption
} from "@/Contracts/Settings";
import {
	AllRolePermissions,
	PricingPlanNames,
	UserRole
} from "@/Contracts/Users";
import ZFileUpload from "@molecules/ZFileUpload";
import { UppyAllowedFileTypes as AllowedFileTypes } from "@molecules/ZFileUpload/interface";
import ZMessage from "@molecules/ZMessage";
import ZSpin from "@molecules/ZSpin";
import {
	currencyFormatter,
	getCurrencySettings,
	percentageFormatter
} from "@/Utils/Currency";
import { Col, Divider, Row, Select, Switch } from "antd";
import { RadioChangeEvent } from "antd/es/radio";
import Button from "antd/lib/button";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import Input from "antd/lib/input";
import InputNumber from "antd/lib/input-number";
import TextArea from "antd/lib/input/TextArea";
import he from "he";
import React, {
	Component,
	Dispatch,
	Fragment,
	ReactNode,
	ReactText,
	SetStateAction,
	useEffect
} from "react";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { AddProductToQuoteBlockDrawer } from "../AddProductToQuoteBlockDrawer";
import BundleProductDrawerTable from "../DocumentProductDrawersComponents/BundleProductDrawerTable";
import DynamicPricingOptions from "../DocumentProductDrawersComponents/DynamicPricingOptions/DynamicPricingOptions";
import { ProductPricingOptions } from "../DocumentProductDrawersComponents/ProductPricingOptions/ProductPricingOptions";
import "./../ConfigureDocumentProductDrawer.less";
import {
	getProductImageUrl,
	processCustomFieldFormValues
} from "@/Utils/common";
import ZDrawer from "@molecules/ZDrawer";
import { useBundlesSettings } from "@/Hooks";
import ZButton from "@atoms/ZButton";
import ZInput from "@molecules/ZInput";
import { InputNumberWidths } from "@atoms/ZInputNumber";

const { ZText } = ZTypography;

export const mapStateToProps = (state: GlobalState) => {
	return {
		isLoading: state.products.isLoading,
		taxCategoriesList: state.user.taxCategoriesList,
		defaultPreferences:
			state.user.userData?.user_settings?.preference_settings
				?.product_preference_settings,
		companyPreferences:
			state.user.userCompany.settings?.preference_settings
				?.product_preference_settings,
		addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen,
		unitOfMeasureMapping:
			state.user.userCompany?.settings.default_unit_of_measure_mapping,
		userData: state.user.userData,
		userCompany: state.user.userCompany,
		suppliers: state.settings.suppliers,
		addOrEditSupplierDrawerOpen: state.settings.addOrEditSupplierDrawerOpen,
		customIntegrationList:
			state.user.userCompany.integrations.custom_distributor,
		customFieldListProduct:
			state.customFields.customFieldListWithoutFormulaFields.item
	};
};

export const mapDispatchToProps = (
	dispatch: ThunkDispatch<GlobalState, any, Action>
) => {
	return {
		addTaxCategoryDrawerOpened: () =>
			dispatch(UsersActionCreators.addTaxCategoryDrawerOpened()),
		addTaxCategoryDrawerClosed: () =>
			dispatch(UsersActionCreators.addTaxCategoryDrawerClosed()),
		showAddSupplierDrawer: () =>
			dispatch(SettingsActionCreators.showAddOrEditSupplierDrawer()),
		hideAddSupplierDrawer: () =>
			dispatch(SettingsActionCreators.hideAddOrEditSupplierDrawer()),
		showProductsLoader: () =>
			dispatch(ProductsActionCreators.showProductsLoader()),
		hideProductsLoader: () =>
			dispatch(ProductsActionCreators.hideProductsLoader())
	};
};

export interface State {
	readonly catalogProductImageUploadModalOpen: boolean;
	readonly bundleAddProductDrawerOpen: boolean;
	readonly createProductButtonVisible: boolean;
	readonly catalogProductImages: string[];
	readonly selectedImages: string[];
	readonly pricing: ProductPricingPlan[] | null;
	readonly selectedDistributor: string | undefined;
	readonly selectedWarehouse: string | undefined;
	readonly activeBundledQuoteProducts: BundledQuoteProduct[];
	readonly editBundledQuoteProductDefaultState: BundledQuoteProduct | null;
	readonly customFieldList: CustomField[];
	readonly vendorCustomFields: CustomField[];
	readonly selectedPricingId?: string | null;
	readonly isAutoUpdatePricingEnabled: boolean;
	readonly isAutoUpdateSwitchEnabled: boolean;
	readonly canEditCostPrice?: boolean;
	readonly selectedDistributors?: DynamicPricingDistributors[] | null;
	readonly roundingDetails?: RoundingDetails;
	readonly retailInfo?: RetailPriceInfo | null;
	readonly distributorLabel?: string | null;
	readonly supplierId?: string | null;
	readonly isAmazonProduct: boolean;
}

interface bundleSettingDetail {
	readonly pricingDisabled: boolean;
	readonly showChildItems: boolean;
	readonly showQuantity: boolean;
	readonly showPrice: boolean;
	readonly setShowChildItems: Dispatch<SetStateAction<boolean>>;
	readonly setShowQuantity: Dispatch<SetStateAction<boolean>>;
	readonly setShowPrice: Dispatch<SetStateAction<boolean>>;
	readonly handleChangeChildItems: () => void;
	readonly handleChangeQuantity: () => void;
	readonly handleChangePrice: () => void;
}

export interface Props
	extends FormComponentProps<QuoteProduct>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	onClose?: (e: any) => void;
	readonly okText: string;
	readonly defaultData:
		| AddQuoteProductCatalogDrawerFormValues
		| EditQuoteProductCatalogDrawerFormValues
		| null;
	readonly onSubmit?: (quoteProduct: QuoteProduct) => void;
	readonly currentActiveDocument: Document | null;
	readonly isAddingProduct?: boolean;
	readonly isBundleProduct?: boolean;
	readonly hasEditProductCostPricePermission?: boolean;
	readonly hasEditBundleCostPricePermission?: boolean;
	readonly hasTaxEditPermission?: boolean;
	readonly isNotABundleProduct?: boolean;
	readonly isSupplierManagementEnabled?: boolean;
	readonly isSupplierSubmitting?: boolean;
	readonly getSuppliers?: () => void;
	readonly getSupplierById?: (supplierId?: string) => void;
	readonly addSupplier?: (
		supplierValues: SupplierFormValues
	) => Promise<Supplier | undefined>;
	readonly allowQuantityZeroOrNegative?: AllowZeroOrNegative;
	readonly isReadOnly?: boolean;
	readonly bundleSettingDetail?: bundleSettingDetail;
	readonly isBundleEnhancementEnabled?: boolean;
	readonly lineItemAddonCurrency?: string;
}

export interface IHandlePricingIdChangeProps {
	value: string | null;
	radioChangeEvent: RadioChangeEvent | null;
}

class DocumentProductConfigureDrawerForm extends Component<Props, State> {
	state: State = {
		catalogProductImageUploadModalOpen: false,
		bundleAddProductDrawerOpen: false,
		createProductButtonVisible: false,
		catalogProductImages: new Array<string>(),
		selectedImages: new Array<string>(),
		pricing: null,
		selectedDistributor: this.props.defaultData?.selected_distributor,
		selectedWarehouse: undefined,
		activeBundledQuoteProducts:
			this.props.defaultData?.bundled_products || [],
		editBundledQuoteProductDefaultState: null,
		customFieldList: new Array<CustomField>(),
		vendorCustomFields: new Array<CustomField>(),
		selectedPricingId: this.props.defaultData?.selected_pricing_id,
		isAutoUpdatePricingEnabled: false,
		isAutoUpdateSwitchEnabled: true,
		canEditCostPrice: true,
		selectedDistributors:
			this.props.defaultData?.auto_price_update_details
				?.selected_distributors,
		roundingDetails:
			this.props.defaultData?.auto_price_update_details?.rounding_details,
		retailInfo: null,
		isAmazonProduct: false
	};

	componentDidMount() {
		const { defaultData } = this.props;
		if (!!defaultData) {
			this.updatePricing(defaultData);
			this.setState({
				customFieldList: this.props.customFieldListProduct
			});
			if (defaultData?.selected_supplier_id) {
				this.props.getSupplierById?.(defaultData?.selected_supplier_id);
			} else {
				this.props.getSuppliers?.();
			}
			const isAmazonProduct =
				!!this.props.userCompany.integrations.amazon_business
					?.connected_user?.length &&
				(this.props.defaultData?.product_source ===
					ProductSource.Amazon ||
					this.props.defaultData?.id?.includes(
						ProductSource.Amazon
					) ||
					(!!this.props.defaultData?.asin &&
						this.props.defaultData?.asin !== "NA"));
			this.setState({
				isAmazonProduct
			});
		}
	}

	componentDidUpdate(prevProps: Props) {
		const { defaultData } = this.props;
		if (
			prevProps.defaultData?.selected_supplier_id !==
			defaultData?.selected_supplier_id
		) {
			if (defaultData?.selected_supplier_id) {
				this.props.getSupplierById?.(defaultData?.selected_supplier_id);
			}
		}
	}

	showAddTaxCategoryDrawer = () => {
		this.props.addTaxCategoryDrawerOpened();
	};

	closeAddTaxCategoryDrawer = () => {
		this.props.addTaxCategoryDrawerClosed();
	};

	fetchProductDetails = async (productId: string) => {
		const productSource =
			this.props.defaultData?.product_source ===
			ProductSource.ZomentumMarketplace
				? "marketplace"
				: productId.startsWith("custom_distributor:")
				? ProductSource.CustomDistributor
				: productId.startsWith("dicker_data:")
				? ProductSource.DickerData
				: productId.startsWith(ProductSource.Etilize)
				? ProductSource.Etilize
				: productId.startsWith(ProductSource.Icecat)
				? ProductSource.Icecat
				: productId.startsWith(ProductSource.Amazon)
				? ProductSource.Amazon
				: "self";
		this.props.showProductsLoader();
		const itemResponse = await getProductDetails(productId, productSource);
		this.props.hideProductsLoader();
		return itemResponse;
	};

	getIntialSelectedPricingId = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	) => {
		if (
			this.props.currentActiveDocument?.status === DocumentStatus.Won ||
			this.props.currentActiveDocument?.status === DocumentStatus.Lost
		) {
			return defaultData?.selected_pricing_id;
		}
		if (
			!!defaultData?.auto_price_update_details?.selected_distributors
				.length
		) {
			return defaultData?.pricing?.find(
				pricing =>
					defaultData.auto_price_update_details
						?.selected_distributors &&
					pricing.distributor_pricing_details?.distributor &&
					!!defaultData.auto_price_update_details?.selected_distributors.find(
						distributor => {
							if (
								distributor.distributor ===
								Distributor.CustomDistributor
							) {
								return (
									distributor
										.selected_custom_distributor_details
										?.selected_custom_distributor_pricing_id ===
									pricing.distributor_pricing_details
										?.custom_distributor_pricing_details
										?.custom_distributor_pricing_id
								);
							}
							return (
								distributor.distributor ===
									pricing.distributor_pricing_details
										?.distributor &&
								pricing.vendor_sku_no ===
									defaultData.vendor_sku_number
							);
						}
					)
			)?.id;
		}
		if (defaultData?.selected_distributor) {
			const selectedPricing = defaultData.pricing?.find(pricing => {
				if (
					pricing.distributor_pricing_details?.distributor ===
					Distributor.CustomDistributor
				) {
					return (
						pricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor_pricing_id ===
						defaultData.selected_custom_distributor_details
							?.selected_custom_distributor_pricing_id
					);
				}

				return (
					pricing.distributor_pricing_details?.distributor ===
						defaultData.selected_distributor &&
					pricing.vendor_sku_no === defaultData.vendor_sku_number
				);
			});
			return selectedPricing?.id;
		} else if (this.state.isAmazonProduct) {
			return defaultData?.pricing?.[0]?.id ?? "";
		} else {
			return defaultData?.selected_pricing_id;
		}
	};

	updatePricing = async (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
	) => {
		if (defaultData.vendor_product_id.includes("dell-quote-import")) {
			this.setState({
				isAutoUpdateSwitchEnabled: false
			});
			return;
		}

		if (defaultData.product_source !== ProductSource.ZomentumMarketplace) {
			defaultData.pricing = await this.fetchPricingIfNeeded(
				defaultData.pricing,
				defaultData.vendor_product_id
			);
		}

		if (
			defaultData.product_source !== ProductSource.ZomentumMarketplace &&
			!defaultData.custom_fields
		)
			defaultData.custom_fields = await this.fetchCustomFieldsIfNeeded(
				defaultData.vendor_product_id
			);

		const defaultPricingId =
			defaultData.selected_distributor === Distributor.CustomDistributor
				? defaultData.pricing?.find(pricing => {
						return (
							pricing.distributor_pricing_details
								?.custom_distributor_pricing_details
								?.custom_distributor_pricing_id ===
							defaultData.selected_custom_distributor_details
								?.selected_custom_distributor_pricing_id
						);
				  })?.id
				: defaultData.selected_pricing_id ||
				  defaultData.pricing?.[0]?.id;
		const selectedPricingData = defaultData.pricing?.find(
			pricing =>
				pricing.id === defaultPricingId &&
				pricing.vendor_sku_no === defaultData.vendor_sku_number
		);

		const { markup, margin } = handleMarkupMargin(
			defaultData.selected_cost_price ||
				selectedPricingData?.cost_price ||
				0,
			defaultData.sell_price || selectedPricingData?.sell_price || 0,
			defaultData.quantity || 1,
			defaultData.should_add_setup_price_to_margin,
			defaultData.selected_setup_price ||
				selectedPricingData?.setup_price ||
				0
		);

		const isEtilizeProduct =
			this.props.defaultData?.product_source === ProductSource.Etilize;

		const productMargin =
			isEtilizeProduct ||
			(this.state.isAmazonProduct && this.props.isAddingProduct)
				? this.props.defaultPreferences?.margin
				: margin;
		const productMarkup =
			isEtilizeProduct ||
			(this.state.isAmazonProduct && this.props.isAddingProduct)
				? this.props.defaultPreferences?.markup
				: markup;

		const calculatedSellPrice = handleSellPriceCalculation(
			this.props.defaultPreferences?.markup ?? 0,
			defaultData.selected_cost_price
		);

		const productSellPrice =
			(this.props.defaultPreferences?.markup ?? 0) > 0 &&
			(this.props.defaultPreferences?.margin ?? 0) > 0 &&
			this.props.isAddingProduct &&
			(isEtilizeProduct || this.state.isAmazonProduct)
				? calculatedSellPrice
				: productMarkup === 0 &&
				  productMargin === 0 &&
				  this.state.isAmazonProduct
				? defaultData?.selected_cost_price
				: defaultData?.sell_price;
		this.props.form.setFieldsValue({
			product_type: defaultData.product_type,
			selected_cost_price: defaultData.selected_cost_price,
			sell_price: productSellPrice,
			margin: productMargin,
			markup: productMarkup
		});
		const selectedPricingId = this.getIntialSelectedPricingId(defaultData);

		const defaultPricing = defaultData.pricing?.find(
			pricing => pricing.id === selectedPricingId
		);

		const retailInfo = defaultPricing?.distributor_pricing_details
			?.retail_price
			? ({
					distributor:
						defaultPricing.distributor_pricing_details?.distributor,
					price: defaultPricing.distributor_pricing_details
						?.retail_price,
					vendor_name:
						defaultPricing.distributor_pricing_details?.vendor_name
			  } as RetailPriceInfo)
			: null;

		const distributorLabelName = defaultPricing?.distributor_pricing_details
			?.distributor
			? defaultPricing?.distributor_pricing_details?.distributor ===
			  Distributor.CustomDistributor
				? defaultPricing?.distributor_pricing_details.vendor_name
				: distributorLabel[
						defaultPricing?.distributor_pricing_details?.distributor
				  ]
			: null;

		this.setState({
			retailInfo,
			distributorLabel: distributorLabelName
		});

		const defaultSelectedDistributor: DynamicPricingDistributors = {
			distributor: defaultData?.selected_distributor as Distributor,
			selected_warehouse: defaultData.selected_warehouse ?? null
		};

		if (
			!defaultData.pricing
				?.map(pricing => pricing.id)
				?.includes(defaultData.selected_pricing_id ?? "") &&
			!this.state.isAmazonProduct &&
			!!defaultData.selected_cost_price &&
			this.props.currentActiveDocument?.status !== DocumentStatus.Won &&
			this.props.currentActiveDocument?.status !== DocumentStatus.Lost
		) {
			const selectedDistributor = defaultData.pricing?.find(
				pricing =>
					pricing.vendor_name ===
						defaultSelectedDistributor.distributor &&
					pricing.vendor_sku_no === defaultData.vendor_sku_number &&
					!!pricing.distributor_pricing_details
			);

			const selectedDistributorCostPrice =
				defaultData.selected_distributor ===
				Distributor.CustomDistributor
					? selectedPricingData?.cost_price ??
					  defaultData.selected_cost_price
					: selectedDistributor?.cost_price ??
					  defaultData.selected_cost_price;

			const selectedDistributorSkuNo =
				selectedDistributor?.vendor_sku_no ??
				defaultData.vendor_sku_number;

			this.props.form.setFieldsValue({
				selected_cost_price: selectedDistributorCostPrice,
				vendor_sku_number: selectedDistributorSkuNo,
				sell_price: defaultData.sell_price
			});
		}

		if (
			defaultSelectedDistributor.distributor ===
			Distributor.CustomDistributor
		) {
			defaultSelectedDistributor.selected_custom_distributor_details =
				defaultData.selected_custom_distributor_details;
		}

		const selectedDistributors = defaultData.auto_price_update_details
			?.is_enabled
			? defaultData.auto_price_update_details?.selected_distributors
			: [defaultSelectedDistributor];

		const isSelfProduct =
			!defaultData.manufacturer_part_no &&
			!this.state.pricing?.[0]?.vendor_sku_no;

		const isAnyDistributorAvailable = defaultData.pricing?.some(
			pricing =>
				!!pricing.vendor_name?.length &&
				!!pricing.distributor_pricing_details
		);

		if (
			isSelfProduct ||
			this.props.isBundleProduct ||
			!isAnyDistributorAvailable
		) {
			this.setState({
				isAutoUpdateSwitchEnabled: false,
				isAutoUpdatePricingEnabled: false
			});
		}

		this.setState({
			catalogProductImages:
				defaultData.item_images || new Array<string>(),
			selectedImages: defaultData.selected_images || new Array<string>(),
			pricing: defaultData.pricing,
			selectedDistributor:
				!!selectedPricingData?.distributor_pricing_details ||
				!!selectedPricingId?.length
					? defaultData.selected_distributor
					: undefined,
			selectedWarehouse:
				defaultData.selected_warehouse ??
				selectedPricingData?.distributor_pricing_details
					?.branch_wise_details[0]?.branch_name,
			selectedPricingId,
			canEditCostPrice: this.props.isBundleProduct
				? true
				: this.props.isNotABundleProduct
				? !selectedPricingId
				: !defaultPricing?.distributor_pricing_details,
			isAutoUpdatePricingEnabled:
				defaultData.auto_price_update_details?.is_enabled ?? false,
			selectedDistributors,
			supplierId: defaultPricing?.supplier_id
		});
	};

	fetchPricingIfNeeded = async (
		pricing: Array<ProductPricingPlan>,
		vendorProductId?: string
	) => {
		try {
			const productId = vendorProductId ?? null;
			if (!productId) {
				throw new Error(
					"Unable to fetch details; There is no Product Id"
				);
			}

			const [itemResponse, quoteItemResponse] = await Promise.all([
				this.fetchProductDetails(productId),
				!this.props.isAddingProduct &&
					this.props.currentActiveDocument?.status !==
						DocumentStatus.Won &&
					this.props.currentActiveDocument?.status !==
						DocumentStatus.Lost &&
					getQuoteBlockProductPricing({
						product_id:
							this.props.defaultData?.vendor_product_id ?? "",
						pricing_id:
							this.props.defaultData?.selected_pricing_id ?? "",
						sku: this.props.defaultData?.vendor_sku_number ?? "",
						mfg_no:
							this.props.defaultData?.manufacturer_part_no ?? ""
					})
			]);

			// check for custom fields
			itemResponse.custom_fields &&
				this.setState({
					vendorCustomFields: itemResponse.custom_fields
				});

			let finalPricing = pricing;

			if (!this.props.isAddingProduct && quoteItemResponse) {
				finalPricing = quoteItemResponse.pricing;
			} else if (
				pricing.length === 0 ||
				!productId.startsWith("etilize")
			) {
				finalPricing = itemResponse.pricing;
			}
			this.setState({ pricing: finalPricing });
			return finalPricing;
		} catch (error) {
			console.error(error);
		}
		return pricing;
	};

	fetchCustomFieldsIfNeeded = async (vendorProductId?: string) => {
		const productId = vendorProductId ?? null;
		if (!productId) {
			ZMessage.error("Unable to fetch details; There is no Product Id");
			return;
		}
		const itemResponse = await this.fetchProductDetails(productId);
		return itemResponse.custom_fields;
	};

	onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		this.props.form.validateFieldsAndScroll(
			{
				scroll: {
					alignWithTop: true
				}
			},
			(
				errors: Record<string, string>,
				documentProductCatalogFormValues: QuoteProduct
			) => {
				if (!errors) {
					this.addDocumentProductCatalog($event, {
						...documentProductCatalogFormValues,
						selected_pricing_id: this.state.selectedPricingId
					});
				} else {
					console.warn(
						"DocumentProductConfigureDrawerOnSubmit",
						errors
					);
				}
			}
		);
	};

	addDocumentProductCatalog = (
		_$event: any,
		documentProductConfigFormValues: QuoteProduct
	) => {
		const { custom_field_form_values, ...formValues } =
			documentProductConfigFormValues;
		const selectedPricingPlan = this.state.pricing?.find(
			pricing => pricing.id === this.state.selectedPricingId
		);

		if (
			this.state?.isAutoUpdatePricingEnabled &&
			!this.state?.selectedDistributors?.length
		) {
			ZMessage.error(
				"Please select at least one distributor to use auto updated pricing"
			);
			return;
		}

		const quantityValue = formValues.quantity;
		const showIndividualPricing =
			formValues.show_individual_product_pricing;
		const defaultQuantity = this.props.defaultData?.quantity;
		const hasQuantityChanged = quantityValue !== defaultQuantity;

		const isQuantityChangedAndIndividualPricingDisabled =
			!showIndividualPricing && hasQuantityChanged;

		const isZeroQuantityNotSupported =
			quantityValue === 0 &&
			!this.props.allowQuantityZeroOrNegative?.zeroQuantity.value &&
			isQuantityChangedAndIndividualPricingDisabled;

		const isNegativeQuantityNotSupported =
			quantityValue < 0 &&
			!this.props.allowQuantityZeroOrNegative?.negativeQuantity.value &&
			isQuantityChangedAndIndividualPricingDisabled;

		if (isZeroQuantityNotSupported) {
			ZMessage.error(
				`Zero quantity is not supported by ${capitalizeFirstLetter(
					this.props.allowQuantityZeroOrNegative?.zeroQuantity?.name
				)}`
			);
			this.props.form.setFieldsValue({ quantity: defaultQuantity });
			return;
		} else if (isNegativeQuantityNotSupported) {
			ZMessage.error(
				`Negative quantity is not supported by ${capitalizeFirstLetter(
					this.props.allowQuantityZeroOrNegative?.negativeQuantity
						.name
				)}`
			);
			this.props.form.setFieldsValue({ quantity: defaultQuantity });
			return;
		}

		let updatedFormValues: QuoteProduct = {
			...this.props.defaultData,
			...formValues,
			selected_images: this.state.selectedImages,
			manufacturer:
				formValues.manufacturer === "NA" ||
				formValues.manufacturer === undefined
					? null
					: formValues.manufacturer,
			manufacturer_part_no:
				formValues.manufacturer_part_no === "NA" ||
				formValues.manufacturer_part_no === undefined
					? null
					: formValues.manufacturer_part_no,
			unit_of_measure: formValues.unit_of_measure,
			selected_distributor: this.state.selectedDistributor,
			selected_billing_period: formValues.selected_billing_period,
			selected_warehouse: this.state.selectedWarehouse,
			item_images: this.state.catalogProductImages || [],
			bundled_products: this.state.activeBundledQuoteProducts || [],
			mandatory_custom_field_check_require: true,
			vendor_name: selectedPricingPlan?.vendor_name,
			vendor_sku_number: formValues?.vendor_sku_number,
			hide_additional_price: !formValues?.selected_setup_price,
			auto_price_update_details: this.state.isAutoUpdatePricingEnabled
				? {
						is_enabled: this.state.isAutoUpdatePricingEnabled,
						selected_distributors:
							this.state.selectedDistributors ?? [],
						rounding_details:
							this.state.roundingDetails ?? new RoundingDetails()
				  }
				: null
		};

		// when toggling individual pricing, it gets ignored
		// by antd form
		updatedFormValues.sell_price = updatedFormValues.sell_price || 0;
		updatedFormValues.quantity = updatedFormValues.quantity || 1;
		if (custom_field_form_values && !!this.state.customFieldList?.length) {
			updatedFormValues = {
				...updatedFormValues,
				custom_fields: processCustomFieldFormValues(
					custom_field_form_values,
					this.state.customFieldList
				)
			};
		}

		if (
			updatedFormValues.selected_distributor ===
			Distributor.CustomDistributor
		) {
			updatedFormValues.selected_custom_distributor_details = {
				custom_distributor_id:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_distributor
						.id || "",
				selected_custom_distributor_pricing_id:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details
						?.custom_distributor_pricing_id || "",
				custom_fields:
					selectedPricingPlan?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_fields ??
					[]
			};
		}

		if (
			this.props.isBundleEnhancementEnabled &&
			this.props.bundleSettingDetail
		) {
			updatedFormValues.hide_child_items_in_quote_block =
				!this.props.bundleSettingDetail.showChildItems;
			updatedFormValues.show_child_quantity =
				this.props.bundleSettingDetail.showQuantity;
			updatedFormValues.show_individual_product_pricing =
				this.props.bundleSettingDetail.showPrice;
		}

		this.props?.onSubmit?.(updatedFormValues);
	};

	getFieldValues = () => {
		return this.props.form.getFieldsValue([
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"margin",
			"markup"
		]);
	};

	onQuantityChange = (newQuantity?: number | string | null): void => {
		if (newQuantity === undefined || newQuantity === null) {
			return;
		}
		const values = this.getFieldValues();
		this.props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				Number(newQuantity),
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	onCostPriceChange = (newCostPrice?: ReactText | null): void => {
		if (newCostPrice === undefined) {
			return;
		}
		const values = this.getFieldValues();
		this.props.form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
		const selectedPricing = this.state.pricing?.find(
			pricing => pricing.id === this.state.selectedPricingId
		);
		if (
			selectedPricing?.distributor_pricing_details ||
			this.state.isAmazonProduct
		) {
			this.setState({
				selectedPricingId: ""
			});
		}
	};

	onMarkupChange = (newMarkup?: ReactText | null): void => {
		if (
			newMarkup === undefined ||
			this.props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = this.getFieldValues();
		this.props.form.setFieldsValue(
			handleMarkupChange(values, Number(newMarkup))
		);
	};

	onMarginChange = (newMargin?: ReactText | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			this.props.form.getFieldValue("selected_cost_price") === 0
		) {
			return;
		}
		const values = this.getFieldValues();

		if (
			Number(newMargin) >= 100 &&
			Number(this.getFieldValues().selected_cost_price) !== 0
		) {
			return;
		}
		this.props.form.setFieldsValue(
			handleMarginChange(values, Number(newMargin))
		);
	};

	onSellPriceChange = (newSellPrice?: ReactText | number | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = this.getFieldValues();
		this.props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	onSetupPriceChange = (newSetupPrice?: ReactText | null): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = this.getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		this.props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	onAddSetupPriceToMarginChange = (e: CheckboxChangeEvent): void => {
		const values = this.getFieldValues();
		this.props.form.setFieldsValue(
			handleMarkupMargin(
				values.selected_cost_price,
				values.sell_price,
				values.quantity,
				e.target.checked,
				values.selected_setup_price
			)
		);
	};

	showCatalogProductImageUploadModal = () => {
		this.setState({ catalogProductImageUploadModalOpen: true });
	};

	closeCatalogProductImageUploadModal = () => {
		this.setState({ catalogProductImageUploadModalOpen: false });
	};

	deleteCatalogProductImage = (imageUrl: string) => {
		ZModal.confirm({
			title: "Are you sure you want to remove this image?",
			onOk: () => {
				this.setState(prevState => ({
					catalogProductImages: prevState.catalogProductImages.filter(
						img => img !== imageUrl
					),
					selectedImages: prevState.selectedImages.filter(
						img => img !== imageUrl
					)
				}));
			}
		});
	};

	uploadCatalogProductImage = (result: UploadResult<UppyFileMetaData>) => {
		if (!!result?.successful?.length) {
			const updatedCatalogImages = result.successful.map(
				image => image.uploadURL
			);

			this.setState(prevState => ({
				catalogProductImages: [
					...prevState.catalogProductImages,
					...updatedCatalogImages
				],
				selectedImages: [
					...prevState.selectedImages,
					...updatedCatalogImages
				]
			}));
		} else if (!!result?.failed?.length) {
			const failedError = result.failed[0].error;
			ZMessage.error(failedError);
		} else {
			ZMessage.error(DefaultErrorMessage);
		}
	};

	handlePricingIdChange = (params: IHandlePricingIdChangeProps) => {
		try {
			const pricing = this.state.pricing;
			const selectedPricingId = this.props.isNotABundleProduct
				? params?.value
				: params?.radioChangeEvent?.target.value;
			const distributor =
				pricing
					?.find(pricing => pricing.id === selectedPricingId)
					?.distributor_pricing_details?.distributor.toLowerCase() ??
				null;
			this.setState({
				selectedDistributor: distributor ?? undefined
			});

			const selectedPricing = pricing?.find(
				p => p.id === selectedPricingId
			);

			this.setState({
				canEditCostPrice: this.props.isNotABundleProduct
					? !!!selectedPricingId
					: !selectedPricing?.distributor_pricing_details
			});
			this.setState({ selectedPricingId });

			const distributorOrCatalog = pricing?.find(
				pricing => pricing.id === selectedPricingId
			);

			/* On pricing change: 
				1. If distributor/catalog is selected, get the supplier & SKU from pricing plan
				2. If custom is selected, retain the supplier id & SKU from the form. \
			*/

			const supplierId = !!distributorOrCatalog?.supplier_id?.length
				? distributorOrCatalog.supplier_id
				: this.props.form.getFieldValue("selected_supplier_id");

			const supplierSKU = !!distributorOrCatalog?.supplier_id?.length
				? distributorOrCatalog.vendor_sku_no
				: this.props.form.getFieldValue("vendor_sku_number");

			this.props.getSupplierById?.(supplierId);

			const margin: number = this.props.form.getFieldValue("margin") ?? 0;
			const markup: number = this.props.form.getFieldValue("markup") ?? 0;

			const calculatedSellPrice = !!selectedPricing
				? !!selectedPricing?.distributor_pricing_details ||
				  (this.state.isAmazonProduct &&
						!!selectedPricing?.amazon_business_pricing_details)
					? markup * 0.01 * (selectedPricing?.cost_price ?? 0) +
					  (selectedPricing?.cost_price ?? 0)
					: selectedPricing?.sell_price
				: markup *
						0.01 *
						(this.props.form.getFieldValue("cost_price") ?? 0) +
				  (this.props.form.getFieldValue("cost_price") ?? 0);

			this.props.form.setFieldsValue({
				selected_supplier_id: supplierId,
				vendor_sku_number: supplierSKU,
				selected_cost_price: selectedPricing?.cost_price || 0,
				selected_setup_price: selectedPricing?.setup_price || 0,
				selected_billing_period:
					selectedPricing?.billing_period ||
					ProductBillingType.OneTime,
				sell_price: calculatedSellPrice,
				margin: margin,
				markup: markup,
				asin: this.props.defaultData?.asin
			});

			const retailInfo = selectedPricing?.distributor_pricing_details
				?.retail_price
				? ({
						distributor:
							selectedPricing.distributor_pricing_details
								?.distributor,
						price: selectedPricing.distributor_pricing_details
							?.retail_price,
						vendor_name:
							selectedPricing.distributor_pricing_details
								.vendor_name
				  } as RetailPriceInfo)
				: null;

			const distributorLabelName = selectedPricing
				?.distributor_pricing_details?.distributor
				? selectedPricing?.distributor_pricing_details?.distributor ===
				  Distributor.CustomDistributor
					? selectedPricing?.distributor_pricing_details.vendor_name
					: distributorLabel[
							selectedPricing?.distributor_pricing_details
								?.distributor
					  ]
				: null;
			this.setState({
				retailInfo,
				distributorLabel: distributorLabelName
			});
		} catch (errors) {
			console.warn(
				"DocumentProductConfigureDrawerHandlePricingIdChange",
				errors
			);
		}
	};

	handleWarehouseChange = (newWarehouse?: string, vendor?: string) => {
		this.setState({ selectedWarehouse: newWarehouse });
		if (this.state.isAutoUpdatePricingEnabled) {
			const updatedDistributors = this.state.selectedDistributors?.map(
				distributor =>
					distributor.distributor === (vendor as Distributor)
						? {
								distributor: vendor as Distributor,
								selected_warehouse: newWarehouse ?? null
						  }
						: distributor
			);
			this.setState({
				selectedDistributors: updatedDistributors
			});
		}
	};

	handleAutoUpdatePricingChange = (autoUpdatePricing: boolean) => {
		this.setState({ isAutoUpdatePricingEnabled: autoUpdatePricing });
	};

	handleRoundingDetailsChange = (newRoundingDetails: RoundingDetails) => {
		this.setState({ roundingDetails: newRoundingDetails });
	};

	handleSelectedDistributorsChange = (
		distributors:
			| {
					distributor: Distributor;
					selected_warehouse: string | null;
					vendor_name?: string;
			  }[]
			| null
	) => {
		const selectedDistributors = distributors?.map(distributor => {
			const distributorDetail: DynamicPricingDistributors = {
				distributor: distributor.distributor,
				selected_warehouse: distributor.selected_warehouse
			};
			if (distributor.distributor === Distributor.CustomDistributor) {
				const distributorId = this.props.customIntegrationList?.find(
					integration => {
						return (
							integration.integration_name ===
							distributor.vendor_name
						);
					}
				)?.id;
				const selectedPricing = this.state.pricing?.find(pricing => {
					return (
						pricing.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor.id === distributorId
					);
				});
				distributorDetail.selected_custom_distributor_details = {
					custom_distributor_id: distributorId || "",
					selected_custom_distributor_pricing_id:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor_pricing_id || "",
					custom_fields:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_fields ?? []
				};
			}
			return distributorDetail;
		});
		this.setState({
			selectedDistributors: !!selectedDistributors?.length
				? selectedDistributors
				: null
		});
	};

	handlePricingsChange = (e: CheckboxChangeEvent) => {
		let selectedDistributors = !!this.state.selectedDistributors?.length
			? [...this.state.selectedDistributors]
			: [];

		if (e.target.checked) {
			const selectedPricing = this.state?.pricing?.find(
				pricing =>
					!!pricing.distributor_pricing_details &&
					pricing.id === e.target.value
			);
			const newDistributor: DynamicPricingDistributors = {
				distributor:
					selectedPricing?.distributor_pricing_details?.distributor ??
					Distributor.Ingram,
				selected_warehouse:
					selectedPricing?.distributor_pricing_details
						?.branch_wise_details?.[0]?.branch_name ?? null
			};
			if (
				selectedPricing?.distributor_pricing_details?.distributor ===
				Distributor.CustomDistributor
			) {
				const distributorId = this.props.customIntegrationList?.find(
					integration =>
						integration.integration_name ===
						selectedPricing?.distributor_pricing_details
							?.vendor_name
				)?.id;
				newDistributor.selected_custom_distributor_details = {
					custom_distributor_id: distributorId || "",
					selected_custom_distributor_pricing_id:
						selectedPricing.distributor_pricing_details
							.custom_distributor_pricing_details
							?.custom_distributor_pricing_id || "",
					custom_fields:
						selectedPricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_fields ?? []
				};
			}

			selectedDistributors?.push(newDistributor);
		} else {
			selectedDistributors =
				this.state.selectedDistributors?.filter(
					(arr: DynamicPricingDistributors) => {
						const pricing = this.state?.pricing?.find(
							pricing =>
								!!pricing.distributor_pricing_details &&
								pricing.id === e.target.value
						);

						if (arr.distributor === Distributor.CustomDistributor) {
							return (
								pricing?.distributor_pricing_details
									?.custom_distributor_pricing_details
									?.custom_distributor.id !==
								arr.selected_custom_distributor_details
									?.custom_distributor_id
							);
						}
						return (
							pricing?.distributor_pricing_details
								?.distributor !== arr.distributor
						);
					}
				) ?? [];
		}
		this.setState({
			selectedDistributors
		});

		const updatedPricingPlans = this.state.pricing?.filter(pricing =>
			selectedDistributors?.find(distributorDetail => {
				if (
					distributorDetail.distributor ===
					Distributor.CustomDistributor
				) {
					return (
						pricing?.distributor_pricing_details
							?.custom_distributor_pricing_details
							?.custom_distributor.id ===
						distributorDetail.selected_custom_distributor_details
							?.custom_distributor_id
					);
				}
				return (
					pricing?.distributor_pricing_details?.distributor ===
					distributorDetail.distributor
				);
			})
		);

		const lowestPricedDistributorSortedArray = updatedPricingPlans?.sort(
			(prev, curr) =>
				prev.cost_price < curr.cost_price
					? -1
					: prev.cost_price > curr.cost_price
					? 1
					: 0
		);
		const lowestPricedDistributorCostPrice =
			lowestPricedDistributorSortedArray?.[0]?.cost_price ?? 0;

		const supplierId = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.supplier_id
			: this.props.form.getFieldValue("selected_supplier_id");

		const supplierSKU = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.vendor_sku_no
			: this.props.form.getFieldValue("vendor_sku_number");

		const calculatedSellPrice =
			this.props.form.getFieldValue("markup") > 0
				? (this.props.form.getFieldValue("markup") *
						lowestPricedDistributorCostPrice) /
						100 +
				  lowestPricedDistributorCostPrice
				: lowestPricedDistributorCostPrice;
		const calculatedSetupPrice = !!lowestPricedDistributorSortedArray?.[0]
			?.supplier_id?.length
			? lowestPricedDistributorSortedArray?.[0]?.setup_price
			: this.props.form.getFieldValue("selected_setup_price");

		this.props.form.setFieldsValue({
			selected_cost_price: lowestPricedDistributorCostPrice,
			sell_price: calculatedSellPrice,
			selected_supplier_id: supplierId,
			selected_setup_price: calculatedSetupPrice,
			vendor_sku_number: supplierSKU
		});

		this.setState({ retailInfo: null });
	};

	onClose = (event: any) => {
		if (this.props.onClose) {
			this.props.onClose(event);
		}
	};

	handleRowChange = (
		selectedVendorProductId: string,
		newProductData: Partial<BundledQuoteProduct>
	) => {
		let hideAdditionalPrice: boolean =
			this.state.activeBundledQuoteProducts.find(
				product => product.vendor_product_id === selectedVendorProductId
			)?.hide_additional_price ?? false;
		if (
			newProductData.selected_setup_price === 0 ||
			newProductData.selected_setup_price
		) {
			hideAdditionalPrice = newProductData.selected_setup_price === 0;
		}
		this.setState(prevState => ({
			activeBundledQuoteProducts:
				prevState.activeBundledQuoteProducts.map(product =>
					product.vendor_product_id === selectedVendorProductId
						? {
								...product,
								...newProductData,
								hide_additional_price: hideAdditionalPrice
						  }
						: product
				)
		}));
	};

	handleRowDelete = (selectedVendorProductId: string) => {
		this.setState(prevState => ({
			activeBundledQuoteProducts:
				prevState.activeBundledQuoteProducts.filter(
					product =>
						product.vendor_product_id !== selectedVendorProductId
				)
		}));
	};

	checkAndAddProductToBundle = (quoteProducts: QuoteProduct[]) => {
		const data = quoteProducts[0];
		const quoteProduct: BundledQuoteProduct = {
			...data,
			is_optional: false,
			is_selected: false
		};

		if (data.product_type === ProductType.Bundle) {
			ZMessage.error("You cannot add a bundle inside another bundle");
			return;
		}

		const alreadyExist = this.state.activeBundledQuoteProducts.find(
			a => a.vendor_product_id === quoteProduct.vendor_product_id
		);

		if (!!alreadyExist) {
			ZModal.confirm({
				title: "Please Confirm",
				content:
					"Product already added to the bundle. Update the product with new values?",
				okText: "Yes",
				onOk: () => {
					this.handleAddProductToBundle(data);
				}
			});
		} else {
			this.handleAddProductToBundle(data);
		}
	};

	handleAddProductToBundle = (data: QuoteProduct) => {
		try {
			const quoteProduct: BundledQuoteProduct = {
				...data,
				is_optional: false,
				is_selected: false
			};

			const productExistingInBundleIndex =
				this.state.activeBundledQuoteProducts.findIndex(
					product =>
						product.vendor_product_id ===
						quoteProduct.vendor_product_id
				);

			this.setState(prevState => ({
				...prevState,
				bundleAddProductDrawerOpen: false,
				activeBundledQuoteProducts:
					productExistingInBundleIndex !== -1
						? [
								...prevState.activeBundledQuoteProducts.map(
									product =>
										product.vendor_product_id ===
										quoteProduct.vendor_product_id
											? quoteProduct
											: product
								)
						  ]
						: [
								...prevState.activeBundledQuoteProducts,
								quoteProduct
						  ]
			}));

			const prevProductPricingValues = this.props.form.getFieldsValue([
				"selected_cost_price",
				"sell_price",
				"quantity"
			]);

			this.props.form.setFieldsValue({
				selected_cost_price:
					Math.abs(prevProductPricingValues.selected_cost_price) +
					data.selected_cost_price * data.quantity,
				sell_price:
					Math.abs(prevProductPricingValues.sell_price) +
					data.sell_price * data.quantity
			});

			ZMessage.success(`${data.name} is added to the bundle`);
		} catch (error) {
			if (error.name !== "ValidationError") {
				console.error(error);
			}
		}
	};

	updateCostPriceFromBundleItems = () => {
		const costPrice =
			this.state.activeBundledQuoteProducts?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total + curr.selected_cost_price * curr.quantity,
				0
			) || 0;

		this.props.form.setFieldsValue({ selected_cost_price: costPrice });
	};

	onSupplierAdd = async (supplierFormValues: SupplierFormValues) => {
		const supplier = await this.props.addSupplier?.(supplierFormValues);
		this.props.getSupplierById?.(supplier?.id);
		this.props.form.setFieldsValue({ selected_supplier_id: supplier?.id });
		return supplier;
	};

	renderCostPriceInputGroup = () => {
		const {
			defaultData,
			defaultPreferences,
			form: { getFieldValue, getFieldDecorator }
		} = this.props;

		const calculatedSellPrice =
			(defaultPreferences?.markup ?? 0) *
				0.01 *
				(defaultData?.selected_cost_price ?? 0) +
			(defaultData?.selected_cost_price ?? 0);

		const productSellPrice =
			(defaultPreferences?.markup ?? 0) > 0 &&
			(defaultPreferences?.margin ?? 0) > 0 &&
			this.props.isAddingProduct
				? calculatedSellPrice
				: defaultData?.sell_price;

		const values = {
			selected_cost_price:
				getFieldValue("selected_cost_price") ??
				defaultData?.selected_cost_price ??
				0,
			sell_price:
				getFieldValue("sell_price") ??
				productSellPrice ??
				defaultData?.sell_price ??
				0,
			quantity: getFieldValue("quantity") ?? defaultData?.quantity ?? 0,
			should_add_setup_price_to_margin:
				getFieldValue("should_add_setup_price_to_margin") ??
				defaultData?.should_add_setup_price_to_margin ??
				0,
			selected_setup_price:
				getFieldValue("selected_setup_price") ??
				defaultData?.selected_setup_price ??
				0,
			product_type:
				getFieldValue("product_type") ?? defaultData?.product_type ?? 0
		};

		const { markup, margin } = handleMarkupMargin(
			values.selected_cost_price,
			values.sell_price,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.selected_setup_price
		);

		const forBundle = defaultData?.product_type === ProductType.Bundle;

		const isAmazonPricingSelected =
			this.state.isAmazonProduct && !!this.state.selectedPricingId;

		const isCatalogOrCustomPricingSelected =
			!!!this.state.selectedDistributor &&
			(!!this.state.selectedPricingId ||
				!!!this.state.selectedPricingId?.length);

		const isCostPricingFieldsDisabled =
			this.props.isReadOnly ||
			(this.props.isBundleEnhancementEnabled &&
				this.props.bundleSettingDetail?.pricingDisabled);

		return (
			<Fragment>
				{!forBundle && (
					<>
						<Col span={24}>
							<Form.Item
								label="Supplier Name"
								labelAlign="left"
								hasFeedback={true}
							>
								{this.props.form.getFieldDecorator(
									"selected_supplier_id",
									{
										rules: [
											{
												required: false
											}
										],
										initialValue:
											defaultData?.selected_supplier_id
									}
								)(
									<ZSupplierSelect
										onChange={supplier =>
											this.setState({
												supplierId: supplier?.toString()
											})
										}
										defaultOptions={this.props.suppliers}
										placeholder="Please enter supplier name"
										disabled={
											!isCatalogOrCustomPricingSelected ||
											this.props.isReadOnly ||
											isAmazonPricingSelected
										}
										dropdownRender={menu => (
											<div>
												{menu}
												<div
													className="add-entity-action"
													onMouseDown={$event => {
														$event.preventDefault();
													}}
													onClick={
														this.props
															.showAddSupplierDrawer
													}
												>
													Add Supplier
												</div>
											</div>
										)}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Supplier SKU"
								labelAlign="left"
								hasFeedback={true}
							>
								{this.props.form.getFieldDecorator(
									"vendor_sku_number",
									{
										rules: [
											{
												required: false
											}
										],
										initialValue:
											defaultData?.vendor_sku_number
									}
								)(
									<Input
										disabled={
											!isCatalogOrCustomPricingSelected ||
											this.props.isReadOnly ||
											isAmazonPricingSelected
										}
										placeholder="Please enter supplier SKU"
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={24}>
							<Col span={24}>
								<Form.Item label="ASIN" labelAlign="left">
									{this.props.form.getFieldDecorator("asin", {
										rules: [
											{
												required: false,
												whitespace: true
											}
										],
										initialValue: defaultData?.asin || "NA"
									})(
										<ZInput
											placeholder="Please enter ASIN"
											disabled={
												!isCatalogOrCustomPricingSelected ||
												this.props.isReadOnly ||
												isAmazonPricingSelected
											}
											className="w-full"
										/>
									)}
								</Form.Item>
							</Col>
						</Col>
					</>
				)}
				<Col span={24}>
					<Form.Item
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
						label="Cost Price"
						labelAlign="left"
					>
						<ZTooltip
							title="Please disable auto update price to edit the cost price."
							showtooltip={this.state.isAutoUpdatePricingEnabled}
						>
							{getFieldDecorator("selected_cost_price", {
								rules: [
									{
										required: true,
										validator: (_, value) => {
											if (Number(value) < 0) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Cost price cannot be less than 0"
													)
												);
											}
											if (!value && value !== 0) {
												return Promise.reject(
													new Error(
														"Cost price cannot be empty"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue:
									defaultData?.selected_cost_price ?? 0
							})(
								<ZInputNumber
									data-testid="selected-cost-price"
									step=".01"
									placeholder="Please enter product cost price"
									min={0}
									onChange={this.onCostPriceChange}
									precision={2}
									disabled={
										this.state.isAutoUpdatePricingEnabled ||
										isCostPricingFieldsDisabled ||
										!isCatalogOrCustomPricingSelected ||
										isAmazonPricingSelected
									}
									addOnAfter={
										this.props.lineItemAddonCurrency ??
										getCurrencySettings().currency
									}
									width={InputNumberWidths.full}
								/>
							)}
						</ZTooltip>
						{forBundle && (
							<ZFeatureAccess
								requiredRole={UserRole.SecondaryRole}
								requiredPlan={PricingPlanNames.IGNITE}
							>
								<Button
									className="sub-action-button"
									type="link"
									onClick={
										this.updateCostPriceFromBundleItems
									}
									disabled={isCostPricingFieldsDisabled}
								>
									<ZText className="text-blue-5 active:text-blue-6">
										<ZIcon
											name={EIcons.COPY}
											size="sm"
											className="mx-1 stroke-current"
										/>
										Copy Product Total
									</ZText>
								</Button>
							</ZFeatureAccess>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Markup"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						<Input.Group>
							{getFieldDecorator("markup", {
								rules: [
									{
										required: false,
										type: "number"
									}
								],
								initialValue: markup
							})(
								<InputNumber
									placeholder="Please enter product Markup"
									onChange={this.onMarkupChange}
									className="has-currency-addon"
									precision={2}
									step={0.1}
									disabled={isCostPricingFieldsDisabled}
								/>
							)}
							<Input
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Margin"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						<Input.Group>
							{getFieldDecorator("margin", {
								rules: [
									{
										required: false,
										type: "number",
										validator(_, value) {
											if (
												Number(value) >= 100 &&
												getFieldValue(
													"selected_cost_price"
												) > 0
											) {
												return Promise.reject(
													new Error(
														"Invalid Entry. Margin cannot be 100% unless cost price is 0"
													)
												);
											}
											return Promise.resolve();
										}
									}
								],
								initialValue: margin
							})(
								<InputNumber
									placeholder="Please enter product Margin"
									max={100}
									onChange={this.onMarginChange}
									className="has-currency-addon"
									precision={2}
									step={0.1}
									disabled={isCostPricingFieldsDisabled}
								/>
							)}
							<Input
								placeholder={"%"}
								disabled
								className="currency-addon"
							/>
						</Input.Group>
					</Form.Item>
				</Col>
			</Fragment>
		);
	};

	updateSellPriceFromBundleItems = () => {
		const sellPrice =
			this.state.activeBundledQuoteProducts?.reduce(
				(total, curr) =>
					curr.is_optional
						? total
						: total + curr.sell_price * curr.quantity,
				0
			) || 0;
		this.props.form.setFieldsValue({ sell_price: sellPrice });
	};

	updateSellPriceByRetailPrice = () => {
		if (this.state.retailInfo?.price) {
			this.props.form.setFieldsValue({
				sell_price: this.state.retailInfo?.price
			});
			this.onSellPriceChange(this.state.retailInfo?.price);
		}
	};

	onSetupFeeLinkedToQuantityChange = (e: CheckboxChangeEvent) => {
		this.props.form.setFieldsValue({
			is_setup_fee_linked_to_quantity: e.target.checked
		});
	};

	updateSetupPriceFromBundleItems = () => {
		const totalSetupPrice =
			this.state.activeBundledQuoteProducts.reduce(
				(total, product) =>
					product.is_optional
						? total
						: total + product.selected_setup_price,
				0
			) || 0;
		this.props.form.setFieldsValue({
			selected_setup_price: totalSetupPrice
		});
	};

	renderSellPriceInputGroup = () => {
		const {
			defaultData,
			form: { getFieldDecorator }
		} = this.props;
		const { isAmazonProduct } = this.state;
		const isEtilizeProduct =
			this.props.defaultData?.product_source === ProductSource.Etilize;

		const forBundle = defaultData?.product_type === ProductType.Bundle;

		const calculatedSellPriceForEtilizeProduct =
			(this.props.defaultPreferences?.markup ?? 0) *
				0.01 *
				(defaultData?.selected_cost_price ?? 0) +
			(defaultData?.selected_cost_price ?? 0);

		const calculatedSellPrice = this.state.pricing?.filter(
			pricing => pricing.id === this.state.selectedPricingId
		)[0]?.sell_price;

		const productSellPrice =
			(this.props.defaultPreferences?.markup ?? 0) > 0 &&
			(this.props.defaultPreferences?.margin ?? 0) > 0 &&
			this.props.isAddingProduct
				? isEtilizeProduct || isAmazonProduct
					? calculatedSellPriceForEtilizeProduct
					: calculatedSellPrice ?? defaultData?.sell_price ?? 0
				: this.props.defaultPreferences?.markup === 0 &&
				  this.props.defaultPreferences?.margin === 0 &&
				  isAmazonProduct
				? defaultData?.selected_cost_price
				: defaultData?.sell_price;

		const showRetailPriceCopy =
			!this.state.isAutoUpdatePricingEnabled &&
			this.state.retailInfo?.price &&
			this.props.isNotABundleProduct;

		const sellPrice = this.props.form.getFieldValue("sell_price");

		const showSampleSellPrice = !!(
			this.state.isAutoUpdatePricingEnabled &&
			this.state.roundingDetails?.is_enabled &&
			sellPrice
		);

		const roundingSample =
			showSampleSellPrice && this.state.roundingDetails
				? round(
						sellPrice,
						this.state.roundingDetails?.precision,
						this.state.roundingDetails?.method
				  )
				: "";

		const isSellPricingFieldsDisabled =
			this.props.isReadOnly ||
			(this.props.isBundleEnhancementEnabled &&
				this.props.bundleSettingDetail?.pricingDisabled);
		const selectedPricing = this.state.pricing?.find(
			pricing => pricing.id === this.state.selectedPricingId
		);
		const calculatedShippingCost =
			selectedPricing?.amazon_business_pricing_details
				?.shipping_cost?.[0] ??
			defaultData?.pricing?.[0]?.amazon_business_pricing_details
				?.shipping_cost?.[0] ??
			0;

		const onAddShippingFeesToSellPrice = (e: CheckboxChangeEvent): void => {
			const productSellPrice =
				this.props.form.getFieldValue("sell_price");
			if (calculatedShippingCost && e.target.checked) {
				const requiredKeys = [
					"selected_cost_price",
					"sell_price",
					"quantity",
					"should_add_setup_price_to_margin",
					"selected_setup_price",
					"product_type"
				];
				const values: Partial<QuoteProduct> =
					this.props.form.getFieldsValue(requiredKeys);
				const { markup, margin } = handleMarkupMargin(
					values.selected_cost_price || 0,
					values.sell_price + calculatedShippingCost || 0,
					values.quantity || 1,
					!!values.should_add_setup_price_to_margin,
					values.selected_setup_price || 0
				);
				this.props.form.setFieldsValue({
					margin,
					markup
				});
			} else if (calculatedShippingCost) {
				this.props.form.setFieldsValue({
					margin: this.props.defaultPreferences?.margin,
					markup: this.props.defaultPreferences?.markup
				});
			}

			if (e.target.checked && productSellPrice) {
				this.props.form.setFieldsValue({
					sell_price: productSellPrice + calculatedShippingCost
				});
			} else if (productSellPrice) {
				this.props.form.setFieldsValue({
					sell_price: productSellPrice - calculatedShippingCost
				});
			}
		};

		return (
			<Fragment>
				<Col span={24}>
					<Form.Item
						label="Billing Period"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("selected_billing_period", {
							initialValue: defaultData?.selected_billing_period
						})(
							<Select<ProductBillingType>
								getPopupContainer={trigger =>
									trigger.parentNode
								}
								disabled={isSellPricingFieldsDisabled}
							>
								{(
									Object.keys(
										productBillingTypeOptions
									) as ProductBillingType[]
								).map(value => (
									<Select.Option key={value} value={value}>
										{productBillingTypeOptions[value]}
									</Select.Option>
								))}
							</Select>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Quantity"
						labelAlign="left"
						hasFeedback={true}
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("quantity", {
							rules: [
								{
									required: true,
									message: "Please enter product quantity"
								}
							],
							initialValue: defaultData?.quantity || 1
						})(
							<ZInputNumber
								placeholder="Please enter product quantity"
								min={Number.MIN_SAFE_INTEGER}
								onChange={this.onQuantityChange}
								precision={0}
								disabled={isSellPricingFieldsDisabled}
								width={InputNumberWidths.full}
							/>
						)}
					</Form.Item>
				</Col>
				<Col span={24}>
					<Form.Item
						label="Sell Price"
						labelAlign="left"
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
						className={
							isAmazonProduct && !!this.state.selectedPricingId
								? "mb-0"
								: ""
						}
					>
						<ZTooltip
							title="Sell price is auto calculated in real-time. Disable auto-update to edit Sell Price."
							showtooltip={
								this.state.isAutoUpdatePricingEnabled &&
								this.props.isNotABundleProduct
							}
						>
							{getFieldDecorator("sell_price", {
								rules: [
									{
										required: true,
										message:
											"This is not a valid price for this product"
									}
								],
								initialValue: productSellPrice ?? 0
							})(
								<ZInputNumber
									data-testid="product-sell-price"
									step=".01"
									placeholder="Please enter product sell price"
									min={0}
									onChange={this.onSellPriceChange}
									precision={2}
									disabled={
										this.state.isAutoUpdatePricingEnabled ||
										isSellPricingFieldsDisabled
									}
									addOnAfter={
										this.props.lineItemAddonCurrency ??
										getCurrencySettings().currency
									}
									width={InputNumberWidths.full}
								/>
							)}
						</ZTooltip>
						{forBundle && (
							<Button
								className="sub-action-button"
								type="link"
								onClick={this.updateSellPriceFromBundleItems}
								disabled={isSellPricingFieldsDisabled}
							>
								<ZText className="text-blue-5 active:text-blue-6">
									<ZIcon
										name={EIcons.COPY}
										size="sm"
										className="mx-1 stroke-current"
									/>
									Copy Product Total
								</ZText>
							</Button>
						)}
					</Form.Item>
				</Col>
				{isAmazonProduct && !!this.state.selectedPricingId && (
					<Col span={20} offset={4}>
						<Form.Item
							className={
								showRetailPriceCopy || showSampleSellPrice
									? "mb-0"
									: ""
							}
						>
							{this.props.form.getFieldDecorator(
								"should_add_shipping_cost_to_sell_price",
								{
									valuePropName: "checked"
								}
							)(
								<ZCheckbox
									onChange={onAddShippingFeesToSellPrice}
									disabled={
										this.props.isReadOnly ||
										!isAmazonProduct ||
										!this.state.selectedPricingId ||
										!calculatedShippingCost
									}
								>
									{`Add Shipping Cost (${currencyFormatter(
										!!this.state.selectedPricingId
											? calculatedShippingCost
											: 0
									)}) to the Selling Price`}
								</ZCheckbox>
							)}
						</Form.Item>
					</Col>
				)}
				{showSampleSellPrice && (
					<Col span={24} offset={4}>
						<div className="mb-6 flex items-center">
							<ZText color={EColors.GRAY} colorVariant={7}>
								<span className="mr-2">Rounding</span>
								{sellPrice}
								{
									<ZIcon
										size="sm"
										className="mb-0.5 mx-2"
										name={EIcons.ARROWRIGHT}
										color={EColors.GRAY}
										colorVariant={6}
									/>
								}
								{roundingSample}
							</ZText>
						</div>
					</Col>
				)}
				{showRetailPriceCopy &&
					this.state.retailInfo &&
					!this.props.isReadOnly && (
						<Col span={24} offset={4}>
							<div className="mb-6 flex items-center">
								<ZText className="text-gray-7">
									{this.state.distributorLabel}{" "}
									{
										ratailPriceLabel[
											this.state.retailInfo.distributor
										]
									}{" "}
									{currencyFormatter(
										this.state.retailInfo.price
									)}
								</ZText>
								<ZIcon
									onClick={this.updateSellPriceByRetailPrice}
									name={Icons.COPY}
									size="sm"
									className="mx-1 stroke-current text-blue-5 active:text-blue-6 cursor-pointer"
								/>
							</div>
						</Col>
					)}
				<Col span={24}>
					<Form.Item
						label="Setup Fee"
						labelAlign="left"
						labelCol={{ span: 4 }}
						wrapperCol={{ span: forBundle ? 14 : 20 }}
					>
						{getFieldDecorator("selected_setup_price", {
							rules: [
								{
									required: true,
									message: "Please enter product setup fee"
								}
							],
							initialValue: defaultData?.selected_setup_price || 0
						})(
							<ZInputNumber
								step=".01"
								placeholder="Please enter product setup fee"
								min={0}
								onChange={this.onSetupPriceChange}
								precision={2}
								disabled={isSellPricingFieldsDisabled}
								addOnAfter={
									this.props.lineItemAddonCurrency ??
									getCurrencySettings().currency
								}
								width={InputNumberWidths.full}
							/>
						)}
						{forBundle && (
							<Button
								className="sub-action-button"
								type="link"
								onClick={this.updateSetupPriceFromBundleItems}
								disabled={isSellPricingFieldsDisabled}
							>
								<ZText className="text-blue-5 active:text-blue-6">
									<ZIcon
										name={EIcons.COPY}
										size="sm"
										className="mx-1 stroke-current"
									/>
									Copy Product Total
								</ZText>
							</Button>
						)}
					</Form.Item>
				</Col>
				<Col span={24} offset={4}>
					<ZTooltip
						placement="bottomLeft"
						title="When enabled, the setup fee will be multiplied by the quantity of this item. If the quantity changes, the total setup fee will adjust accordingly."
					>
						<Form.Item labelAlign="left" wrapperCol={{ span: 24 }}>
							{getFieldDecorator(
								"is_setup_fee_linked_to_quantity",
								{
									valuePropName: "checked",
									initialValue:
										!!defaultData?.is_setup_fee_linked_to_quantity
								}
							)(
								<ZCheckbox
									onChange={
										this.onSetupFeeLinkedToQuantityChange
									}
									checkboxOption="Link Setup Fee to Quantity"
								/>
							)}
						</Form.Item>
					</ZTooltip>
				</Col>
			</Fragment>
		);
	};

	handleAddBundledProductClose = () =>
		this.setState({ bundleAddProductDrawerOpen: false });
	handleAddBundledProductOpen = () =>
		this.setState({ bundleAddProductDrawerOpen: true });
	handleCreateProductOpen = () =>
		this.setState({ createProductButtonVisible: true });

	handleCreateProductAdded = () => {
		this.setState({
			createProductButtonVisible: false,
			bundleAddProductDrawerOpen: true
		});
	};

	handleOnClose = () => this.setState({ createProductButtonVisible: false });

	handleEditBundledProductDrawerOpen = (
		editBundledQuoteProductDefaultState: BundledQuoteProduct
	) => this.setState({ editBundledQuoteProductDefaultState });

	handleEditBundledProductDrawerClose = () =>
		this.setState({ editBundledQuoteProductDefaultState: null });

	handleEditBundledProductDrawerSubmit = ({ id, ...data }: QuoteProduct) => {
		const {
			editBundledQuoteProductDefaultState,
			activeBundledQuoteProducts
		} = this.state;
		const newData: BundledQuoteProduct = {
			...editBundledQuoteProductDefaultState,
			...data,
			is_optional: !!editBundledQuoteProductDefaultState?.is_optional,
			is_selected: !!editBundledQuoteProductDefaultState?.is_selected
		};

		const newActiveBundledQuoteProducts = activeBundledQuoteProducts.map(
			a =>
				a.vendor_product_id === newData.vendor_product_id ? newData : a
		);

		this.setState({
			editBundledQuoteProductDefaultState: null,
			activeBundledQuoteProducts: newActiveBundledQuoteProducts
		});
	};

	handleProductVisible = () => {
		const values: Partial<QuoteProduct> = this.props.form.getFieldsValue([
			"hide_child_items_in_quote_block"
		]);
		this.props.form.setFieldsValue({
			hide_child_items_in_quote_block:
				!values.hide_child_items_in_quote_block
		});
	};

	onCustomFieldChange = (customField: CustomField) => {
		// check if this is a parent custom field
		const childCustomField = this.state.customFieldList?.find(
			childCustomField =>
				childCustomField.parent_field_info?.parent_id === customField.id
		);
		if (childCustomField) {
			this.props.form.setFieldsValue({
				[`custom_field_form_values.${childCustomField.display_name}`]:
					undefined
			});
		}
	};

	getCustomFieldDropdownValues = (customField: CustomField): string[] => {
		// check if this is a child custom field
		if (customField.parent_field_info?.allowed_values) {
			const parent = this.state.customFieldList?.find(
				parentCustomField =>
					parentCustomField.id ===
					customField.parent_field_info?.parent_id
			);
			if (parent) {
				const parentFormItemValue = this.props.form.getFieldValue(
					`custom_field_form_values.${parent.display_name}`
				);
				if (parentFormItemValue) {
					const key = Object.keys(
						customField.parent_field_info.allowed_values
					)?.find(childValue => childValue === parentFormItemValue);
					if (key) {
						return customField.parent_field_info.allowed_values[
							key
						];
					}
				}
			}
		} else {
			return customField.allowed_dropdown_values;
		}
		return new Array<string>();
	};

	getInitialTaxCategoryId = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	): string | undefined => {
		if (
			defaultData?.tax_category_id &&
			defaultData?.tax_category_id?.length > 0
		) {
			const defaultTaxCategory = this.props.taxCategoriesList.find(
				taxCategory => taxCategory.id === defaultData.tax_category_id
			);
			if (!defaultTaxCategory || defaultTaxCategory?.disabled) {
				return;
			}
			return defaultData?.tax_category_id;
		} else if (defaultData?.product_source === ProductSource.Etilize) {
			return this.props.taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						ProductType.Hardware
					)
			)?.id;
		} else if (
			defaultData?.product_type &&
			defaultData?.product_type?.length > 0
		) {
			return this.props.taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.includes(
						defaultData?.product_type as ProductType
					)
			)?.id;
		}

		return undefined;
	};

	getInitialUnitOfMeasure = (
		defaultData:
			| AddQuoteProductCatalogDrawerFormValues
			| EditQuoteProductCatalogDrawerFormValues
			| null
	) => {
		if (!!defaultData?.unit_of_measure?.length) {
			return defaultData.unit_of_measure;
		} else if (!!defaultData?.product_type?.length) {
			return this.props.unitOfMeasureMapping?.find(
				mapping => mapping.item_type === defaultData.product_type
			)?.unit_of_measure;
		}
		return undefined;
	};

	onProductTypeChange = (productType?: string) => {
		if (productType) {
			const tax_category_id = this.props.taxCategoriesList?.find(
				taxCategory =>
					!taxCategory.disabled &&
					taxCategory.default_for_types?.findIndex(
						defaultProductType =>
							defaultProductType === (productType as ProductType)
					) > -1
			)?.id;
			const unit_of_measure = this.props.unitOfMeasureMapping?.find(
				mapping => mapping.item_type === productType
			)?.unit_of_measure;
			this.props.form.setFieldsValue({
				tax_category_id,
				unit_of_measure
			});
		}
	};

	updateBundleProductOrder = (bundleProducts: BundledQuoteProduct[]) => {
		this.setState({
			activeBundledQuoteProducts: bundleProducts
		});
	};

	getProductFormItem = (customField: CustomField): ReactNode => {
		const { defaultData } = this.props;
		const { pricing, isAmazonProduct } = this.state;
		const requiredKeys = [
			"selected_cost_price",
			"sell_price",
			"quantity",
			"should_add_setup_price_to_margin",
			"selected_setup_price",
			"show_individual_product_pricing",
			"hide_child_items_in_quote_block",
			"product_type"
		];
		const values: Partial<QuoteProduct> =
			this.props.form.getFieldsValue(requiredKeys);
		const { margin } = handleMarkupMargin(
			values.selected_cost_price || 0,
			values.sell_price || 0,
			values.quantity || 1,
			!!values.should_add_setup_price_to_margin,
			values.selected_setup_price || 0
		);
		const marginAmount =
			((values.sell_price || 0) - (values.selected_cost_price || 0)) *
				(values.quantity || 1) +
			(!!values.should_add_setup_price_to_margin
				? values.selected_setup_price || 0
				: 0);

		const isBundleProduct = values.product_type === ProductType.Bundle;

		const isAmazonPricingSelected =
			isAmazonProduct && !!this.state.selectedPricingId;

		switch (customField.system_field_id) {
			case "item_type": {
				return (
					<Fragment>
						<Col span={24}>
							<Form.Item
								label="Type"
								labelAlign="left"
								hasFeedback={true}
							>
								{this.props.form.getFieldDecorator(
									"product_type",
									{
										rules: [
											{
												required: true,
												message:
													"Please select product type"
											}
										],
										initialValue: values.product_type ?? ""
									}
								)(
									<Select<ProductType>
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										disabled={
											this.props.isReadOnly ||
											values.product_type ===
												ProductType.Bundle
										}
										allowClear={true}
										placeholder="Please select product type"
										onChange={this.onProductTypeChange}
									>
										<Select.Option
											value={ProductType.Hardware}
										>
											Hardware
										</Select.Option>
										<Select.Option
											value={ProductType.Software}
										>
											Software
										</Select.Option>
										<Select.Option
											value={ProductType.Service}
										>
											Service
										</Select.Option>
										<Select.Option
											value={ProductType.Labor}
										>
											Labor
										</Select.Option>
										<Select.Option
											value={ProductType.Bundle}
											disabled
										>
											Bundle
										</Select.Option>
									</Select>
								)}
							</Form.Item>
						</Col>
					</Fragment>
				);
			}
			case "item_item_images": {
				const fileUploadModalProps: IModalProps = {
					visible: this.state.catalogProductImageUploadModalOpen,
					title: "Upload image"
				};
				return (
					<Fragment>
						<Col span={24}>
							<Form.Item label="Product Images" labelAlign="left">
								<div className="product-image-checkbox-container">
									{this.state.catalogProductImages.map(
										(catalogProductImage, index) => (
											<ZCheckbox
												key={index}
												className="product-image-checkbox"
												disabled={this.props.isReadOnly}
												value={catalogProductImage}
												checked={this.state.selectedImages.includes(
													catalogProductImage
												)}
												onChange={e => {
													const { checked } =
														e.target;
													this.setState(
														prevState => ({
															selectedImages:
																checked
																	? [
																			...prevState.selectedImages,
																			catalogProductImage
																	  ]
																	: prevState.selectedImages.filter(
																			img =>
																				img !==
																				catalogProductImage
																	  )
														})
													);
												}}
											>
												<ZImage
													className="product-image"
													src={getProductImageUrl(
														catalogProductImage
													)}
													data-testid="product-thumbnail-image"
													alt="Product Thumbnail"
													fallback={fallbackImageURL}
												/>
												{!this.props.isReadOnly && (
													<span
														className="product-image-delete-btn"
														role="button"
														tabIndex={0}
														aria-label="Delete product image"
														onMouseDown={e => {
															e.preventDefault();
															e.stopPropagation();
														}}
														onClick={e => {
															e.stopPropagation();
															this.deleteCatalogProductImage(
																catalogProductImage
															);
														}}
														onKeyDown={e => {
															if (
																e.key ===
																	"Enter" ||
																e.key === " "
															) {
																e.preventDefault();
																e.stopPropagation();
																this.deleteCatalogProductImage(
																	catalogProductImage
																);
															}
														}}
													>
														<Icon type="delete" />
													</span>
												)}
											</ZCheckbox>
										)
									)}
									{!this.props.isReadOnly && (
										<div
											className="product-image-file-upload"
											onClick={
												this
													.showCatalogProductImageUploadModal
											}
										>
											<Icon type="upload" />
											<span>Upload</span>
										</div>
									)}
								</div>
								{process.env.NODE_ENV !== "test" && (
									<ZFileUpload
										maxNumberOfFiles={5}
										minNumberOfFiles={1}
										useModal={fileUploadModalProps}
										useURL
										allowedFileTypes={[
											AllowedFileTypes.IMAGE
										]}
										maxFileSize={26214400}
										allowMultipleUploads={true}
										onCancel={
											this
												.closeCatalogProductImageUploadModal
										}
										onUploadSuccess={result => {
											this.closeCatalogProductImageUploadModal();
											this.uploadCatalogProductImage(
												result
											);
										}}
									/>
								)}
							</Form.Item>
						</Col>
					</Fragment>
				);
			}
			case "item_description": {
				return (
					<Col span={24}>
						<Form.Item
							label="Description"
							labelAlign="left"
							hasFeedback={true}
						>
							{this.props.form.getFieldDecorator("description", {
								initialValue: he.decode(
									defaultData?.description ?? ""
								)
							})(
								<TextArea
									placeholder="Please enter product description"
									autoSize={{ minRows: 3 }}
									allowClear={true}
									className="zomentum-sm-textarea"
									disabled={this.props.isReadOnly}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_pricing": {
				return <Fragment />;
			}
			case "item_product_category": {
				return <Fragment />;
			}
			case "item_manufacturer": {
				return (
					!isBundleProduct && (
						<Col span={24}>
							<Form.Item label="Manufacturer" labelAlign="left">
								{this.props.form.getFieldDecorator(
									"manufacturer",
									{
										rules: [
											{
												required: false,
												whitespace: true
											}
										],
										initialValue:
											defaultData?.manufacturer || "NA"
									}
								)(
									<Input
										placeholder="Please enter manufacturer name."
										disabled={
											this.props.isReadOnly ||
											isAmazonPricingSelected
										}
									/>
								)}
							</Form.Item>
						</Col>
					)
				);
			}
			case "item_manufacturer_part_no": {
				return (
					!isBundleProduct && (
						<Col span={24}>
							<Form.Item label="Mfg. Number" labelAlign="left">
								{this.props.form.getFieldDecorator(
									"manufacturer_part_no",
									{
										rules: [
											{
												required: false,
												whitespace: true
											}
										],
										initialValue:
											defaultData?.manufacturer_part_no ||
											"NA"
									}
								)(
									<Input
										placeholder="Please enter manufacturer part number"
										disabled={
											this.props.isReadOnly ||
											isAmazonPricingSelected
										}
									/>
								)}
							</Form.Item>
						</Col>
					)
				);
			}
			case "item_vendor_name": {
				return <Fragment />;
			}
			case "item_vendor_sku_no": {
				return <Fragment />;
			}
			case "item_tax_category_id": {
				const initialTaxCategoryId =
					this.getInitialTaxCategoryId(defaultData);
				return (
					<Col span={24}>
						<Form.Item
							label="Tax Category"
							labelAlign="left"
							hasFeedback={true}
						>
							{this.props.form.getFieldDecorator(
								"tax_category_id",
								{
									rules: [
										{
											required:
												!!this.props
													.currentActiveDocument
													?.tax_region_id,
											message: "Please enter tax category"
										}
									],
									initialValue: initialTaxCategoryId
								}
							)(
								<TaxSelect
									data-testid="document-product-tax-category"
									getPopupContainer={trigger =>
										trigger.parentNode
									}
									placeholder="Please select tax category"
									loading={this.props.isLoading}
									disabled={
										this.props.isLoading ||
										this.props.isReadOnly
									}
									dropdownRender={menu => (
										<div>
											{menu}
											<RoleBasedDisplayWrapper
												requiredRole={
													UserRole.PrimaryRole
												}
											>
												{this.props
													.hasTaxEditPermission && (
													<div
														className="add-entity-action"
														onMouseDown={$event => {
															$event.preventDefault();
														}}
														onClick={
															this
																.showAddTaxCategoryDrawer
														}
													>
														Add Tax Category
													</div>
												)}
											</RoleBasedDisplayWrapper>
										</div>
									)}
								/>
							)}
						</Form.Item>
					</Col>
				);
			}
			case "item_should_add_setup_price_to_margin": {
				return (
					<Fragment>
						{isBundleProduct && (
							<Fragment>
								{!values.show_individual_product_pricing &&
									!this.props.isBundleEnhancementEnabled && (
										<Col span={24} className="mb-3">
											<Row justify="end">
												<ZButton
													type="link"
													onClick={
														this
															.handleProductVisible
													}
													disabled={
														this.props.isReadOnly
													}
												>
													{!!values.hide_child_items_in_quote_block
														? `Show`
														: `Hide`}{" "}
													All Products
												</ZButton>
											</Row>
										</Col>
									)}
								<Col span={24} className="mb-6">
									<BundleProductDrawerTable
										data={
											this.state
												.activeBundledQuoteProducts
										}
										onRowChange={this.handleRowChange}
										onRowDelete={this.handleRowDelete}
										onAddProductButtonClick={
											this.handleAddBundledProductOpen
										}
										onCreateProductButtonClick={
											this.handleCreateProductOpen
										}
										onOpenEditDrawer={
											this
												.handleEditBundledProductDrawerOpen
										}
										isReadOnly={this.props.isReadOnly}
										updateBundleProductOrder={
											this.updateBundleProductOrder
										}
									/>
								</Col>
								{this.props.isBundleEnhancementEnabled ? (
									<Col>
										<div className="mb-6 mt-6">
											<ZText className="mr-4">
												Show child items
											</ZText>
											<Switch
												onChange={
													this.props
														?.bundleSettingDetail
														?.handleChangeChildItems
												}
												size="small"
												checked={
													this.props
														?.bundleSettingDetail
														?.showChildItems
												}
											/>
											{this.props?.bundleSettingDetail
												?.showChildItems && (
												<>
													<div className="flex items-center mt-1">
														<ZIcon
															name={
																EIcons.INFOCIRCLE
															}
															size="sm"
															className="m-0"
														/>
														<ZText
															colorVariant={8}
															level={0}
															className="ml-2"
														>
															Please note that you
															will not be able to
															edit the product
															bundle pricing
															fields if the{" "}
															<ZText
																colorVariant={9}
																level={0}
															>
																Show Price
															</ZText>{" "}
															is toggled on.
														</ZText>
													</div>
													<div className="ml-9 mt-4">
														<ZText className="mr-4">
															Show quantity
														</ZText>
														<Switch
															onChange={
																this.props
																	?.bundleSettingDetail
																	?.handleChangeQuantity
															}
															size="small"
															checked={
																this.props
																	?.bundleSettingDetail
																	?.showQuantity
															}
														/>
														<div className="mb-2"></div>
														<ZText className="mr-9">
															Show price
														</ZText>
														<Switch
															onChange={
																this.props
																	?.bundleSettingDetail
																	?.handleChangePrice
															}
															size="small"
															checked={
																this.props
																	?.bundleSettingDetail
																	?.showPrice
															}
														/>
													</div>
												</>
											)}
										</div>
									</Col>
								) : (
									<Col span={24} className="mb-3">
										<Row justify="end">
											{this.props.form.getFieldDecorator(
												"show_child_quantity",
												{
													initialValue:
														!!defaultData?.show_child_quantity,
													valuePropName: "checked"
												}
											)(
												<ZCheckbox
													disabled={
														this.props.isReadOnly
													}
												>
													Display individual quantity
													on quote block
												</ZCheckbox>
											)}
										</Row>
									</Col>
								)}
								<Col span={24} className="mb-6">
									<Row className="pricing-data-box">
										{!this.props
											.isBundleEnhancementEnabled && (
											<Col span={24} className="mb-6">
												{this.props.form.getFieldDecorator(
													"show_individual_product_pricing",
													{
														initialValue:
															!!defaultData?.show_individual_product_pricing,
														valuePropName: "checked"
													}
												)(
													<ZCheckbox
														disabled={
															this.props
																.isReadOnly
														}
													>
														Show individual product
														pricing
													</ZCheckbox>
												)}
											</Col>
										)}
										<Row
											className={`${
												!this.props
													.isBundleEnhancementEnabled
													? this.props.form.getFieldValue(
															"show_individual_product_pricing"
													  )
														? "hidden"
														: ""
													: ""
											}`}
										>
											{this.renderCostPriceInputGroup()}
											<Divider className="mb-6" />
											{this.renderSellPriceInputGroup()}
											<Col span={24}>
												{this.props.form.getFieldDecorator(
													"should_add_setup_price_to_margin",
													{
														valuePropName:
															"checked",
														initialValue:
															!!defaultData?.should_add_setup_price_to_margin
													}
												)(
													<ZCheckbox
														onChange={
															this
																.onAddSetupPriceToMarginChange
														}
														disabled={
															this.props
																.isReadOnly ||
															(this.props
																.isBundleEnhancementEnabled &&
																this.props
																	.bundleSettingDetail
																	?.pricingDisabled)
														}
													>
														Add 'Setup Fee' to
														Margin
													</ZCheckbox>
												)}
												<div className="margin-preview">
													<strong>
														Item Margin:
													</strong>{" "}
													<span>
														{percentageFormatter(
															margin,
															100
														)}{" "}
														(
														{currencyFormatter(
															marginAmount
														)}
														)
													</span>
												</div>
											</Col>
										</Row>
									</Row>
								</Col>
							</Fragment>
						)}
						{!isBundleProduct && (
							<Fragment>
								<Col span={24}>
									{this.props.isNotABundleProduct ? (
										<DynamicPricingOptions
											handlePricingsChange={
												this.handlePricingsChange
											}
											pricing={pricing}
											selectedPricingId={
												this.state.selectedPricingId
											}
											selectedWarehouse={
												this.state.selectedWarehouse
											}
											handlePricingIdChange={
												this.handlePricingIdChange
											}
											handleWarehouseChange={
												this.handleWarehouseChange
											}
											handleAutoUpdatePricingChange={
												this
													.handleAutoUpdatePricingChange
											}
											handleSelectedDistributorsChange={
												this
													.handleSelectedDistributorsChange
											}
											isAutoUpdateSwitchEnabled={
												this.state
													.isAutoUpdateSwitchEnabled
											}
											isAutoUpdatePricingEnabled={
												this.state
													.isAutoUpdatePricingEnabled
											}
											selectedDistributors={
												this.state.selectedDistributors
											}
											roundingDetails={
												this.state.roundingDetails
											}
											handleRoundingDetailsChange={
												this.handleRoundingDetailsChange
											}
											calculatedSellPrice={this.props.form.getFieldValue(
												"sell_price"
											)}
											isReadOnly={this.props.isReadOnly}
											isAmazonProduct={isAmazonProduct}
											costPrice={
												defaultData?.selected_cost_price
											}
											currentActiveDocument={
												this.props.currentActiveDocument
											}
										/>
									) : (
										<ProductPricingOptions
											pricing={pricing}
											selectedPricingId={
												this.state.selectedPricingId
											}
											selectedWarehouse={
												this.state.selectedWarehouse
											}
											handlePricingIdChange={
												this.handlePricingIdChange
											}
											handleWarehouseChange={
												this.handleWarehouseChange
											}
											isReadOnly={this.props.isReadOnly}
										/>
									)}
								</Col>
								{this.renderCostPriceInputGroup()}
								{this.renderSellPriceInputGroup()}
								<Col span={24}>
									{this.props.form.getFieldDecorator(
										"should_add_setup_price_to_margin",
										{
											valuePropName: "checked",
											initialValue:
												!!defaultData?.should_add_setup_price_to_margin
										}
									)(
										<ZCheckbox
											onChange={
												this
													.onAddSetupPriceToMarginChange
											}
											disabled={this.props.isReadOnly}
										>
											Add 'Setup Fee' to Margin
										</ZCheckbox>
									)}
									<div className="margin-preview my-6">
										<strong>Item Margin:</strong>{" "}
										<span>
											{percentageFormatter(margin, 100)} (
											{currencyFormatter(marginAmount)})
										</span>
									</div>
								</Col>
							</Fragment>
						)}
					</Fragment>
				);
			}
			case "item_show_individual_product_pricing":
			case "item_tax_type":
				return <Fragment />;
			case "item_unit_of_measure": {
				const uomOptions = customField.allowed_dropdown_values?.map(
					value => ({
						value,
						name: value
					})
				);
				const initialUnitOfMeasure =
					this.getInitialUnitOfMeasure(defaultData);
				return (
					<>
						<Col span={24}>
							<Form.Item
								label="UOM"
								labelAlign="left"
								hasFeedback={true}
								className="mb-2"
							>
								{this.props.form.getFieldDecorator(
									"unit_of_measure",
									{
										initialValue: initialUnitOfMeasure,
										rules: [
											{
												required:
													customField
														?.mandatory_field_details?.[
														CustomFieldMandatoryFieldDetailsValues
															.FOR_UI
													],
												message:
													"Please select the unit of measure"
											}
										]
									}
								)(
									<ZSelect
										placeholder="Please select the unit of measure"
										allowClear
										selectOptions={uomOptions}
										disabled={this.props.isReadOnly}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={20} offset={4} className="mb-6">
							<ProductUOMWarning
								currentUOM={this.props.form.getFieldValue(
									"unit_of_measure"
								)}
								parentProductId={
									defaultData?.vendor_product_id ?? null
								}
								productSource={defaultData?.product_source}
							/>
						</Col>
					</>
				);
			}

			case "quickbooks_item_type": {
				const quickbooksCustomField =
					this.state.vendorCustomFields?.find(
						customField =>
							customField.system_field_id ===
							"quickbooks_item_type"
					);

				const itemQuickbooksItemTypeValues =
					this.props.defaultData?.custom_fields?.find(
						customField =>
							customField.system_field_id ===
							"quickbooks_item_type"
					)?.values;

				const values =
					itemQuickbooksItemTypeValues ??
					quickbooksCustomField?.values;

				const isReadonly = !!values?.length || this.props.isReadOnly;

				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={this.props.form}
							customField={customField}
							values={values}
							shouldShowTooltip
							customFieldList={this.state.customFieldList}
							isReadonly={isReadonly}
							formAlignment={{
								labelCol: { span: 4 },
								wrapperCol: { span: 20 }
							}}
						/>
					</Col>
				);
			}

			case "item_asin": {
				return <Fragment />;
			}

			default: {
				const productCustomField = defaultData?.custom_fields?.find(
					field => field.display_name === customField.display_name
				);
				let values = productCustomField?.values;

				const selectedPricing = pricing?.find(
					pricing => pricing.id === this.state.selectedPricingId
				);
				const customDistributorCustomFields =
					selectedPricing?.distributor_pricing_details
						?.custom_distributor_pricing_details?.custom_fields;

				if (!!customDistributorCustomFields?.length) {
					values = customDistributorCustomFields.find(
						field => field.id === productCustomField?.id
					)?.values;
				}
				const isEtilizeProduct = isEtilizeVendorProductId(
					this.props.defaultData?.vendor_product_id ?? ""
				);
				const isRealtimeSync =
					this.props.userCompany?.integrations.connectwise
						?.sync_option === SyncOption.RealTimeSync;

				const isReadonly =
					this.props.isReadOnly ||
					(isEtilizeProduct &&
						isRealtimeSync &&
						!this.props.isAddingProduct);
				return (
					<Col span={24}>
						<RenderCustomFieldFormItem
							form={this.props.form}
							customField={customField}
							values={values}
							customFieldList={this.state.customFieldList}
							isReadonly={isReadonly}
							formAlignment={{
								labelCol: { span: 4 },
								wrapperCol: { span: 20 }
							}}
						/>
					</Col>
				);
			}
		}
	};

	render() {
		const {
			defaultData,
			form: { getFieldDecorator }
		} = this.props;
		const { editBundledQuoteProductDefaultState } = this.state;
		getFieldDecorator("product_type", {
			initialValue: defaultData?.product_type
		});
		getFieldDecorator("hide_child_items_in_quote_block", {
			initialValue: defaultData?.hide_child_items_in_quote_block
		});
		const isEditBundledProductDrawerVisible =
			!!editBundledQuoteProductDefaultState;

		const handleTaxCategoryDrawerOnSubmit = (id: string) => {
			this.props.form.setFieldsValue({
				tax_category_id: id
			});
		};

		return (
			<Fragment>
				<AddTaxCategoryDrawer
					visible={this.props.addTaxCategoryDrawerOpen}
					onClose={this.closeAddTaxCategoryDrawer}
					handleTaxCategoryDrawerOnSubmit={
						handleTaxCategoryDrawerOnSubmit
					}
				/>
				<Form
					layout="horizontal"
					labelCol={{ span: 4 }}
					wrapperCol={{ span: 20 }}
					onSubmit={this.onSubmit}
					className="add-document-product-catalog-drawer-form"
					noValidate
				>
					<ZSpin spinning={this.props.isLoading}>
						<Row className="drawer-form-container pb-4">
							<Col span={24}>
								<Form.Item
									label="Title"
									labelAlign="left"
									hasFeedback={true}
								>
									{getFieldDecorator("name", {
										rules: [
											{
												required: true,
												message:
													"Please enter product name",
												whitespace: true
											}
										],
										initialValue: defaultData?.name ?? ""
									})(
										<Input
											placeholder="Please enter product name"
											disabled={this.props.isReadOnly}
										/>
									)}
								</Form.Item>
							</Col>
							{this.state.customFieldList?.map(customField => {
								return (
									<Fragment key={customField.id}>
										{this.getProductFormItem(customField)}
									</Fragment>
								);
							})}
						</Row>
					</ZSpin>

					{!this.props.isReadOnly && (
						<div className="ant-drawer-footer">
							<Button
								type="default"
								htmlType="button"
								onClick={this.onClose}
								className="mr-2"
							>
								Cancel
							</Button>
							<Button
								type="primary"
								data-testid="submit-button"
								htmlType="submit"
								disabled={this.props.isLoading}
							>
								{this.props.okText}
							</Button>
						</div>
					)}
				</Form>
				<AddProductToQuoteBlockDrawer
					visible={this.state.bundleAddProductDrawerOpen}
					onClose={this.handleAddBundledProductClose}
					onSubmit={this.checkAndAddProductToBundle}
					itemType={this.props.defaultData?.product_type ?? undefined}
					document={this.props.currentActiveDocument}
				/>
				<AddProductDrawerV2
					shouldRedirectToProduct={false}
					visible={this.state.createProductButtonVisible}
					onClose={this.handleOnClose}
					onAdd={this.handleCreateProductAdded}
					drawerState={EProductDrawerState.ADD}
				/>
				<ZAddSupplier
					visible={this.props.addOrEditSupplierDrawerOpen}
					closeDrawer={this.props.hideAddSupplierDrawer}
					addSupplier={this.onSupplierAdd}
					isFormLoading={this.props.isSupplierSubmitting}
				/>
				<ZDrawer
					visible={isEditBundledProductDrawerVisible}
					closable={true}
					destroyOnClose={true}
					onClose={this.handleEditBundledProductDrawerClose}
					className="document-product-drawer edit-document-product-drawer zomentum-drawer"
					title={
						<Row>
							<Col span={24}>
								<div className="drawer-title-container">
									<div className="drawer-title">
										Configure Product
									</div>
								</div>
							</Col>
						</Row>
					}
				>
					<ConnectedDocumentProductConfigureDrawerForm
						okText="Update Product"
						defaultData={
							(!!editBundledQuoteProductDefaultState &&
								EditQuoteProductCatalogDrawerFormValues.fromBundledQuoteProduct(
									editBundledQuoteProductDefaultState
								)) ||
							null
						}
						onClose={this.handleEditBundledProductDrawerClose}
						onSubmit={this.handleEditBundledProductDrawerSubmit}
						currentActiveDocument={this.props.currentActiveDocument}
						hasEditBundleCostPricePermission={
							this.props.hasEditBundleCostPricePermission
						}
						lineItemAddonCurrency={this.props.lineItemAddonCurrency}
						isBundleProduct={true}
						isReadOnly={this.props.isReadOnly}
					/>
				</ZDrawer>
			</Fragment>
		);
	}
}

const ConnectedDocumentProductConfigureDrawerForm = connect(
	mapStateToProps,
	mapDispatchToProps
)(
	Form.create<Props>({
		name: "configure_document_product_catalog_form"
	})((props: Props) => {
		const editProductPermission = [
			AllRolePermissions.QUOTE_BLOCK_ADD,
			AllRolePermissions.QUOTE_BLOCK_UPDATE,
			AllRolePermissions.QUOTE_BLOCK_EDIT_ALL
		];

		const hasEditProductCostPricePermission = useFeatureAccess(
			PricingPlanNames.IGNITE,
			UserRole.UserRole,
			editProductPermission
		);

		const hasEditBundleCostPricePermission = useFeatureAccess(
			PricingPlanNames.IGNITE,
			UserRole.UserRole,
			[
				...editProductPermission,
				AllRolePermissions.QUOTE_BLOCK_BUNDLE_ADD,
				AllRolePermissions.QUOTE_BLOCK_BUNDLE_UPDATE,
				AllRolePermissions.QUOTE_BLOCK_BUNDLE_EDIT_ALL
			]
		);
		const hasTaxEditPermission = useFeatureAccess(
			PricingPlanNames.IGNITE,
			UserRole.UserRole,
			[AllRolePermissions.TAX_SETUP_UPDATE]
		);

		const isNotABundleProduct = !props.isBundleProduct;

		const { addSupplier, isSubmitting, getSuppliers, getSupplierById } =
			useAddOrEditSupplier();
		const movePreferencesToGlobal = useFeatureFlag(
			"move_preferences_global"
		);
		const enable_bundles_enchancements = useFeatureFlag(
			"enable_bundles_enchancements"
		);

		const {
			pricingDisabled,
			showChildItems,
			showQuantity,
			showPrice,
			setShowChildItems,
			setShowQuantity,
			setShowPrice,
			handleChangeChildItems,
			handleChangeQuantity,
			handleChangePrice
		} = useBundlesSettings();

		const bundleSettingDetail = {
			pricingDisabled,
			showChildItems,
			showQuantity,
			showPrice,
			setShowChildItems,
			setShowQuantity,
			setShowPrice,
			handleChangeChildItems,
			handleChangeQuantity,
			handleChangePrice
		};

		useEffect(() => {
			if (enable_bundles_enchancements && props.defaultData) {
				setShowChildItems(
					!props.defaultData.hide_child_items_in_quote_block
				);
				setShowQuantity(!!props.defaultData.show_child_quantity);
				setShowPrice(
					!!props.defaultData?.show_individual_product_pricing
				);
			}
		}, [props.defaultData]);

		const allowZeroOrNegativeQuantity = useAllowZeroOrNegativeQuantity();

		return (
			<DocumentProductConfigureDrawerForm
				{...props}
				isReadOnly={props.isReadOnly ?? false}
				hasEditProductCostPricePermission={
					hasEditProductCostPricePermission
				}
				hasEditBundleCostPricePermission={
					hasEditBundleCostPricePermission
				}
				hasTaxEditPermission={hasTaxEditPermission}
				isNotABundleProduct={isNotABundleProduct}
				addSupplier={addSupplier}
				getSuppliers={getSuppliers}
				getSupplierById={getSupplierById}
				isSupplierSubmitting={isSubmitting}
				allowQuantityZeroOrNegative={allowZeroOrNegativeQuantity}
				defaultPreferences={
					movePreferencesToGlobal
						? props.companyPreferences
						: props.defaultPreferences
				}
				bundleSettingDetail={bundleSettingDetail}
				isBundleEnhancementEnabled={enable_bundles_enchancements}
			/>
		);
	})
);

export default ConnectedDocumentProductConfigureDrawerForm;
